<template>
  <MakerCartGuideLayout
    pageTitle="Conducting a Maker Activity"
    heroImage="/images/maker-cart-guide/rectangle-4689.png"
    previousUrl="/makercart/guide/6"
    nextUrl="/makercart/guide/8"
  >
    <div class="x-guide7-content">
      <!-- Intro Section -->
      <div class="x-intro-section">
        <h2 class="x-section-title">Role 1: Designing Suitable Activities</h2>
        <p class="x-section-text">
          Designing engaging and effective maker activities is paramount to
          successful maker education. This section will explore key
          considerations for crafting activities that are both ambitious and
          attainable, while providing a framework for varying learner control
          and fostering deep thinking through the use of thinking routines.
        </p>
      </div>

      <!-- Navigation Card -->
      <div class="x-nav-card">
        <div class="x-card-header-bar">
          <div class="x-card-title-area">
            <h2 v-if="currentCardIndex === 0" class="x-card-title">
              Set Activity to be Ambitious Yet Attainable, Scaffold
            </h2>
            <h2 v-if="currentCardIndex === 1" class="x-card-title">
              Give Students Enough Time to Communicate, Fail, Iterate, and Come
              Out with the Final Product
            </h2>
            <h2 v-if="currentCardIndex === 2" class="x-card-title">
              Vary the Learner Control
            </h2>
            <h2 v-if="currentCardIndex === 3" class="x-card-title">
              Use Thinking Routines
            </h2>
          </div>

          <div class="x-card-controls">
            <button
              class="x-control-btn"
              :class="{ 'x-btn-disabled': currentCardIndex === 0 }"
              @click="goToPreviousCard"
              :disabled="currentCardIndex === 0"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15 18L9 12L15 6"
                  :stroke="currentCardIndex === 0 ? '#4C4DDC' : '#FFFFFF'"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
            <button
              class="x-control-btn"
              :class="{ 'x-btn-disabled': currentCardIndex === 3 }"
              @click="goToNextCard"
              :disabled="currentCardIndex === 3"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9 18L15 12L9 6"
                  :stroke="currentCardIndex === 3 ? '#4C4DDC' : '#FFFFFF'"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>

        <div class="x-card-divider"></div>

        <div class="x-card-body">
          <!-- Card 1 Content -->
          <div v-if="currentCardIndex === 0" class="x-card-content">
            <p>
              A rule of thumb while designing Maker Projects could be “Low
              Floor, High Ceiling, Wide Wall”. Starter activities should be easy
              for beginners, but should also create opportunities for students
              to be involved in increasingly complex projects over time. Maker
              projects also need to support the diverse interests and learning
              styles of our students by providing platforms for them to engage
              with the material in different ways, so that it is engaging and
              meaningful for all.
            </p>
            <p>
              Multi-week programs could have students start off with small
              activities during which there is more structure, instructions to
              help introduce a skill or concept, before moving on to prompted
              challenges, during which students are expected to explore the
              design process to propose solutions to different problems.
              Educators can then provide a more open-ended environment in which
              they can drive their own projects they are more interested in.
            </p>
          </div>

          <!-- Card 2 Content -->
          <div v-if="currentCardIndex === 1" class="x-card-content">
            <p>
              Time management is crucial in classroom activity. Many teachers we
              work with tell us they have limited time to implement Maker
              programs in their schools, except as enrichment programs or very
              brief recess time activities. Even so, there is a need to make
              sure the students succeed on their own merits (versus feeling that
              they only succeeded because the teacher helped them) so that they
              can feel the sense of satisfaction and joy of learning. Hence, the
              struggle and failures in between are very critical.
            </p>
          </div>

          <!-- Card 3 Content -->
          <div v-if="currentCardIndex === 2" class="x-card-content">
            <ol>
              <li>
                Who has control of the question or problem being addressed
              </li>
              <li>
                Who has control over the procedure or aspects of the procedure
                (for example, what materials to use, the way in which data are
                recorded)?
              </li>
              <li>
                Who has control over the outcome? (Is it an open-ended problem
                with many possible solutions, or is there only one right
                answer?)
              </li>
            </ol>

            <p>
              As learners become more self-directed, how does the teacher's role
              change? It's important for participants to realize that less
              control by the teacher does not mean giving up responsibility for
              guiding learning.
            </p>
            <p>
              As the teacher turns over more control to the learner, the
              teacher's role changes but doesn't diminish. The teacher continues
              to play a very active role, determining what kinds of experiences
              to provide, the topic of study, the prompts to get learners
              started, what materials will be used, and so on.
            </p>
            <p>
              Most importantly, the teacher still provides facilitation to
              students during the activity.
            </p>
          </div>

          <!-- Card 4 Content -->
          <div v-if="currentCardIndex === 3" class="x-card-content">
            <p>
              Routines exist in all classrooms; Classrooms have routines that
              serve to manage student behavior and interactions, to organizing
              the work of learning, and to establish rules for communication and
              discourse. Classrooms also have routines that structure the way
              students go about the process of learning.
            </p>

            <p>
              The following routines are designed to assist teachers or leaders
              in implementing an environment of learning in which learners and
              teachers make their thinking 'visible' or explicit.
            </p>

            <p>
              These are adapted from <strong>Project Zero</strong> at the
              Harvard Graduate School of Education. (Detailed descriptions of
              each follows.)
            </p>

            <div class="x-tabs">
              <span
                class="x-tab"
                :class="{
                  'x-tab-active':
                    activeTab === 'synthesizing-and-organizing-ideas',
                }"
                @click="setActiveTab('synthesizing-and-organizing-ideas')"
              >
                Synthesizing and Organizing Ideas
              </span>
              <span
                class="x-tab"
                :class="{
                  'x-tab-active': activeTab === 'digging-deeper-into-ideas',
                }"
                @click="setActiveTab('digging-deeper-into-ideas')"
              >
                Digging Deeper into Ideas
              </span>
              <span
                class="x-tab"
                :class="{
                  'x-tab-active':
                    activeTab === 'developing-sensitivity-to-design',
                }"
                @click="setActiveTab('developing-sensitivity-to-design')"
              >
                Developing Sensitivity to Design
              </span>
              <span
                class="x-tab"
                :class="{ 'x-tab-active': activeTab === 'introducing-ideas' }"
                @click="setActiveTab('introducing-ideas')"
              >
                Introducing ideas
              </span>
            </div>

            <!-- Tab Content 1 -->
            <div
              v-if="activeTab === 'synthesizing-and-organizing-ideas'"
              class="x-tab-content"
            >
              <div class="x-table-wrapper">
                <table class="x-table">
                  <thead>
                    <tr>
                      <th colspan="3">Developing Sensitivity to Design</th>
                    </tr>
                    <tr>
                      <th>Routines</th>
                      <th>Type of thinking involved</th>
                      <th>Steps</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>CSI: Color, Symbol, Image</td>
                      <td>
                        Capturing the 'heart' of an idea or concept through
                        metaphors and visual connections.
                      </td>
                      <td>
                        <ol>
                          <li>
                            As you are reading/listening/watching, make note of
                            things that you find interesting, important, or
                            insightful. When you finish, choose 3 of these items
                            that most stand out for you.
                            <ul>
                              <li>
                                Choose a colour that best represents or captures
                                the essence of that idea.
                              </li>
                              <li>
                                Build a symbol that best represents or captures
                                the essence of that idea.
                              </li>
                              <li>
                                Choose an image that best represents or captures
                                the essence of that idea.
                              </li>
                            </ul>
                          </li>
                          <li>
                            With a partner or group first share your colour and
                            tell why you chose it. Repeat sharing until everyone
                            has shared their Colour, Symbol, and Image.
                          </li>
                        </ol>
                      </td>
                    </tr>
                    <tr>
                      <td>Micro-Lab Protocol</td>
                      <td>
                        Individual and group exercise to focus attention,
                        reflect on and analyze a topic.
                      </td>
                      <td>
                        <ol>
                          <li>Identify a topic or question for discussion.</li>
                          <li>
                            Students build a model to represent their thoughts.
                          </li>
                          <li>
                            Divide the students into groups of 3 or 4. Each
                            group counts off.
                          </li>
                          <li>
                            All of the #1's in each group share ideas for 1-2
                            minutes.
                          </li>
                          <li>Call for silence for 20-30 seconds.</li>
                          <li>Repeat for each person.</li>
                          <li>
                            Allow 5-10 minutes for whole group discussion.
                          </li>
                          <li>Each group shares with the entire class.</li>
                        </ol>
                      </td>
                    </tr>
                    <tr>
                      <td>I Used to Think...; Now I Think...</td>
                      <td>Reflect on how one's thinking has changed</td>
                      <td>
                        <ol>
                          <li>
                            Have students write a response (or build a model)
                            using each of the sentence stems:
                            <ul>
                              <li>I used to think...</li>
                              <li>But now, I think...</li>
                            </ul>
                          </li>
                        </ol>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Tab Content 2 -->
            <div
              v-if="activeTab === 'digging-deeper-into-ideas'"
              class="x-tab-content"
            >
              <div class="x-table-wrapper">
                <table class="x-table">
                  <thead>
                    <tr>
                      <th colspan="3">Developing Sensitivity to Design</th>
                    </tr>
                    <tr>
                      <th>Routines</th>
                      <th>Type of thinking involved</th>
                      <th>Steps</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Tug of War</td>
                      <td>
                        Perspective taking, reasoning, identifying complexities
                      </td>
                      <td>
                        <ol>
                          <li>
                            First, present students with a fairness dilemma.
                            Present an actual rope.
                          </li>
                          <li>
                            Second, identify the controversy, which factors are
                            “pulling” at either side.
                          </li>
                          <li>
                            Third, engage students by asking them to think about
                            why each factor is “tugging” at one side,
                            identifying the strongest arguments.
                          </li>
                          <li>
                            Lastly, push students further by asking “what if”
                            questions.
                          </li>
                        </ol>
                      </td>
                    </tr>
                    <tr>
                      <td>Step Inside</td>
                      <td>
                        Practicing taking the perspective of another point of
                        view.
                      </td>
                      <td>
                        <p>Build a model that represents:</p>
                        <ul>
                          <li>What can the person or thing perceive?</li>
                          <li>
                            What might the person or thing know about or
                            believe?
                          </li>
                          <li>What might the person or thing care about?</li>
                        </ul>
                      </td>
                    </tr>
                    <tr>
                      <td>Chalk Talk</td>
                      <td>
                        Brainstorming ideas, questions or plans regarding a
                        topic or problem.
                      </td>
                      <td>
                        <ol>
                          <li>
                            What ideas come to mind when you think about this
                            idea, question, or topic? Build a model that
                            represents that.
                          </li>
                          <li>
                            What connections can you make to others’ thoughts?
                          </li>
                        </ol>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Tab Content 3 -->
            <div
              v-if="activeTab === 'developing-sensitivity-to-design'"
              class="x-tab-content"
            >
              <div class="x-table-wrapper">
                <table class="x-table">
                  <thead>
                    <tr>
                      <th colspan="3">Developing Sensitivity to Design</th>
                    </tr>
                    <tr>
                      <th>Routines</th>
                      <th>Type of thinking involved</th>
                      <th>Steps</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Parts, Purposes, Complexities</td>
                      <td>Make careful and detailed observations</td>
                      <td>
                        <p>Choose an object or system and ask:</p>
                        <ul>
                          <li>What are its parts?</li>
                          <li>What are its purposes?</li>
                          <li>What are its complexities?</li>
                        </ul>
                      </td>
                    </tr>
                    <tr>
                      <td>Parts, People, Interactions</td>
                      <td>
                        Stimulate curiosity, raises questions, surfaces areas
                        for further inquiry, and introduces systems thinking.
                      </td>
                      <td>
                        <p>Identify a system and ask:</p>
                        <ul>
                          <li>What are the parts of the system?</li>
                          <li>Who are the people connected to the system?</li>
                          <li>How do the people in the system interact?</li>
                          <li>
                            How does a change in one element affect the rest?
                          </li>
                        </ul>
                      </td>
                    </tr>
                    <tr>
                      <td>Parts, Perspectives, Me</td>
                      <td>
                        Consider its various viewpoints, users, and
                        stakeholders, and reflecting on their own connections
                        and involvement
                      </td>
                      <td>
                        <p>Choose an object or system and ask:</p>
                        <ul>
                          <li>What are its parts?</li>
                          <li>What perspectives can you look at it from?</li>
                          <li>
                            How are you involved? What connections do you have?
                          </li>
                        </ul>
                      </td>
                    </tr>
                    <tr>
                      <td>Think, Feel, Care</td>
                      <td>
                        Perspective taking, raises questions, and surfaces areas
                        for further inquiry
                      </td>
                      <td>
                        <p>
                          Choose a variety of people within a system and
                          consider:
                        </p>
                        <ul>
                          <li>
                            <b>Think:</b> How does this person understand this
                            system?
                          </li>
                          <li>
                            <b>Feel:</b> What is this person's emotional
                            response?
                          </li>
                          <li><b>Care:</b> What are this person's values?</li>
                        </ul>
                      </td>
                    </tr>
                    <tr>
                      <td>Imagine if...</td>
                      <td>
                        Encourages divergent and convergent thinking; finding
                        opportunity
                      </td>
                      <td>
                        <p>
                          Consider the parts, purposes, and people, then ask:
                        </p>
                        <ol>
                          <li>
                            In what ways could it be made to be more effective?
                          </li>
                          <li>
                            In what ways could it be made to be more efficient?
                          </li>
                          <li>
                            In what ways could it be made to be more ethical?
                          </li>
                          <li>
                            In what ways could it be made to be more beautiful?
                          </li>
                        </ol>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Tab Content 4 -->
            <div v-if="activeTab === 'introducing-ideas'" class="x-tab-content">
              <div class="x-table-wrapper">
                <table class="x-table">
                  <thead>
                    <tr>
                      <th colspan="3">Developing Sensitivity to Design</th>
                    </tr>
                    <tr>
                      <th>Routines</th>
                      <th>Type of thinking involved</th>
                      <th>Steps</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>See-Think (Build) - Wonder</td>
                      <td>
                        Wondering about and describing something ambiguous,
                        complex or new.
                      </td>
                      <td>
                        <ol>
                          <li>What do you see?</li>
                          <li>What do you think about that? Build a model.</li>
                          <li>What does it make you wonder?</li>
                        </ol>
                      </td>
                    </tr>
                    <tr>
                      <td>Think (Build) - Puzzle - Explore</td>
                      <td>
                        Helping students identify what they already know or
                        think...
                      </td>
                      <td>
                        <ol>
                          <li>What do you think you know? Build a model.</li>
                          <li>What questions or puzzles do you have?</li>
                          <li>How can you explore this topic?</li>
                        </ol>
                      </td>
                    </tr>
                    <tr>
                      <td>Chalk Talk</td>
                      <td>Brainstorming ideas, questions or plans...</td>
                      <td>
                        <ol>
                          <li>What ideas come to mind? Build a model.</li>
                          <li>What connections can you make?</li>
                        </ol>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <!-- End Card 4 Content -->
        </div>
      </div>
    </div>
  </MakerCartGuideLayout>
</template>

<script setup>
import { ref } from "vue";
import MakerCartGuideLayout from "@/components/maker-cart/MakerCartGuideLayout.vue";

const currentCardIndex = ref(0);
const activeTab = ref("synthesizing-and-organizing-ideas");
const maxCards = 4;

const goToPreviousCard = () => {
  if (currentCardIndex.value > 0) {
    currentCardIndex.value--;
  }
};

const goToNextCard = () => {
  if (currentCardIndex.value < maxCards - 1) {
    currentCardIndex.value++;
  }
};

const setActiveTab = (tab) => {
  activeTab.value = tab;
};
</script>

<style scoped>
.x-guide7-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.x-intro-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 0;
}

.x-section-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-xl);
  line-height: 32px;
  color: var(--primary-blue);
  margin: 0;
}

.x-section-text {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-medium);
  line-height: 24px;
  letter-spacing: 0.08px;
  color: #3b4648;
  margin: 0;
}

/* Nav Card Styles */
.x-nav-card {
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
  border: 1px solid #d8dae5;
  border-radius: 16px;
  overflow: hidden;
  padding: 16px;
}

.x-card-header-bar {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
  width: 100%;
  margin-bottom: 16px;
}

.x-card-title-area {
  flex: 1;
}

.x-card-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-xl);
  line-height: 32px;
  color: var(--primary-blue);
  margin: 0;
  white-space: pre-wrap;
}

.x-card-controls {
  display: flex;
  gap: 4px;
  align-items: center;
}

.x-control-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 12px;
  border: none;
  background-color: #4c4ddc;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.x-control-btn:hover:not(.x-btn-disabled) {
  opacity: 0.9;
}

.x-btn-disabled {
  background-color: transparent;
  border: 1px solid #4c4ddc;
  cursor: not-allowed;
}

.x-card-divider {
  width: 100%;
  height: 1px;
  background-color: #d8dae5;
  margin-bottom: 16px;
}

.x-card-body {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.x-card-content {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-medium);
  line-height: 24px;
  letter-spacing: 0.08px;
  color: #3b4648;
}

.x-card-content p {
  margin: 0 0 16px 0;
}

.x-card-content p:last-child {
  margin: 0;
}

.x-card-content ol,
.x-card-content ul {
  margin: 0 0 16px 24px;
  padding: 0;
}

.x-card-content li {
  margin-bottom: 8px;
}

/* Tabs */
.x-tabs {
  display: flex;
  gap: 16px;
  border-bottom: 1px solid #d8dae5;
  margin-bottom: 16px;
  overflow-x: auto;
}

.x-tab {
  padding: 8px 16px;
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-medium);
  color: #6c737f;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  white-space: nowrap;
}

.x-tab-active {
  color: var(--primary-blue);
  border-bottom-color: var(--primary-blue);
}

/* Tables */
.x-table-wrapper {
  overflow-x: auto;
}

.x-table {
  width: 100%;
  border-collapse: collapse;
}

.x-table th,
.x-table td {
  border: 1px solid #d8dae5;
  padding: 12px;
  text-align: left;
  font-family: "League Spartan", sans-serif;
}

.x-table th {
  background-color: #f5f6fa;
  font-weight: 700;
  color: var(--primary-blue);
}

.x-table th[colspan="3"] {
  text-align: center;
}

@media (max-width: 768px) {
  .x-card-header-bar {
    flex-direction: column-reverse;
    gap: 8px;
    align-items: flex-start;
  }

  .x-card-controls {
    align-self: flex-end;
  }

  .x-control-btn {
    width: 40px;
    height: 40px;
  }

  .x-nav-card {
    width: 100%;
  }

  .x-card-title {
    white-space: normal;
  }
}
</style>
